import type { ComplianceRequirementCard } from "@regpulse/contracts";
import { DOMAINS } from "./RegulatoryLens";

interface Props {
  cards: ComplianceRequirementCard[];
}

export function CountingGuide({ cards }: Props) {
  const applicable = cards.filter((c) => c.applicabilityCategory === "applicable");
  const hidden = cards.filter((c) => c.applicabilityCategory === "hidden_by_config");
  const unknown = cards.filter((c) => c.applicabilityCategory === "unknown_needs_config");
  const placeholders = applicable.filter((c) => c.lifecycleState === "PLACEHOLDER");
  const visibleApplicable = applicable.filter((c) => c.lifecycleState !== "PLACEHOLDER");
  const zeroDomainExplanations = DOMAINS.map((domain) => {
    const domainCards = cards.filter((c) => c.regulatoryDomain === domain);
    const domainApplicable = domainCards.filter((c) => c.applicabilityCategory === "applicable");
    const domainHidden = domainCards.filter((c) => c.applicabilityCategory === "hidden_by_config");
    const domainUnknown = domainCards.filter((c) => c.applicabilityCategory === "unknown_needs_config");
    if (domainApplicable.length > 0) return null;
    if (domainCards.length === 0) {
      return `${domain}: 0 applicable; no mapped cards in this result set.`;
    }
    return `${domain}: 0 applicable; ${domainHidden.length} hidden by config, ${domainUnknown.length} needs config.`;
  }).filter((x): x is string => x !== null);

  return (
    <details
      data-testid="counting-guide"
      style={{
        background: "var(--color-surface-elevated)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-8)",
        padding: "var(--space-12)",
      }}
    >
      <summary
        style={{
          cursor: "pointer",
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        How counts and mappings work
      </summary>
      <div
        style={{
          marginTop: "var(--space-8)",
          display: "grid",
          gap: "var(--space-8)",
          fontSize: 12,
          color: "var(--color-text-secondary)",
          lineHeight: 1.45,
        }}
      >
        <p style={{ margin: 0 }}>
          <strong>This result set:</strong>{" "}
          Total cards: {cards.length}; Applicable total: {applicable.length}; Visible applicable: {visibleApplicable.length}; Placeholder / curation-gap applicable: {placeholders.length}; Hidden by config: {hidden.length}; Needs config: {unknown.length}.
        </p>
        <p style={{ margin: 0 }}>
          <strong>Applicable total includes placeholder / curation-gap cards.</strong>{" "}
          These are real applicable candidates, but they are not shown in the
          main lifecycle lanes until the source text is curated.
        </p>
        <p style={{ margin: 0 }}>
          <strong>Visible applicable excludes placeholder / curation-gap cards.</strong>{" "}
          The Lifecycle board and heatmap use this visible count, so it can be
          lower than the Applicability split.
        </p>
        <p style={{ margin: 0 }}>
          <strong>Domain chips count applicable cards for the current configuration.</strong>{" "}
          A zero can mean no mapped rules, hidden by config, needs config, or
          mapped elsewhere.
        </p>
        <ul style={{ margin: 0, paddingLeft: "var(--space-16)" }}>
          <li><code>STD-*</code> controls obligation kind: Evidence standard; domain still follows the rule's regulatoryDomain mapping.</li>
          <li><code>MF-*</code> controls obligation kind: market_force and is currently mapped to Market Rating.</li>
          <li><code>ECO-*</code> controls obligation kind: ecosystem_signal and is currently mapped to Ecosystem Interop.</li>
          <li>CO2, tax, fleet, ZEV and purchase-incentive rules → C · Sales Economics.</li>
          <li>R155 / R156 may still share Cybersecurity until the OTA & SUMS domain split is implemented.</li>
        </ul>
        {zeroDomainExplanations.length > 0 && (
          <div>
            <strong>Zero-count domains in this result:</strong>
            <ul style={{ margin: "var(--space-4) 0 0 0", paddingLeft: "var(--space-16)" }}>
              {zeroDomainExplanations.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </details>
  );
}
